/* ============================================================
   Method & sources

   A product whose claim is "verified, cited, current" has to publish
   its own workings — the assumptions, the sources, and above all the
   limits. This page is the credibility surface: if it reads as candid,
   the numbers elsewhere are trusted; if it reads as marketing, they are
   not.
   ============================================================ */

import { html, raw } from '../shared/html.js';
import { icon } from '../shared/icons.js';
import { REGULATIONS, statusOf, STATUS_META } from '../data/regulations.js';
import { BENCHMARKS, BENCHMARK_AS_OF } from '../data/benchmarks.js';
import { DOMAINS } from '../data/domains.js';
import { TOOLS } from '../data/tools.js';
import { longDate, shortDate } from '../shared/format.js';
import { router } from '../shared/router.js';
import { EditionStrip } from '../components/EditionStrip.js';

export function AboutPage() {
  const page = document.createElement('div');
  /* page-prose: this page is explanatory copy laid out in cards, and the
     paragraphs inside them are running prose that needs a measure — the
     smallest of them ran to 96 characters a line. Scoped to the page
     rather than added to .card, which is also the shell for result
     panels and short labels all over the site. */
  page.className = 'container-narrow page page-prose animate-fade-in';

  const live = TOOLS.filter((t) => t.status === 'live').length;
  const inForce = REGULATIONS.filter((r) => statusOf(r) === 'in-force').length;
  const corridors = Object.keys(BENCHMARKS).length;

  page.innerHTML = html`
    <header class="page-head">
      <h1>Method &amp; sources</h1>
      <p>
        How every figure in this product is arrived at, where it comes from, and — the part
        that matters most — what it cannot tell you.
      </p>
    </header>

    <section class="card card-tinted" style="margin-bottom: var(--ca-space-6);">
      <div class="row" style="margin-bottom: var(--ca-space-2);">
        <span style="color: var(--ca-text-tertiary);">${raw(icon('lock', 16))}</span>
        <span style="font-size: var(--ca-text-sm); font-weight: var(--ca-weight-medium);">
          What happens to your data
        </span>
      </div>
      <p style="font-size: var(--ca-text-sm); color: var(--ca-text-secondary); margin-bottom: var(--ca-space-3);">
        Nothing you type is transmitted. Every calculation runs in this browser; every saved
        result is written to this device's local storage and nowhere else. There are no
        accounts, no analytics, no cookies and no third-party scripts.
      </p>
      <p style="font-size: var(--ca-text-xs); color: var(--ca-text-tertiary);">
        This is enforced rather than promised: the app ships a content security policy that
        blocks every off-origin request, which is also why it uses no web fonts and no icon
        CDN. You can verify it in your browser's network tab — after the first load there is
        nothing to see. Review or erase everything under
        <button class="link-inline" id="to-saved" type="button">Saved work</button>.
      </p>
    </section>

    <section class="section">
      <h2 class="section-title">The diagnostic index</h2>
      <div class="card">
        <p style="font-size: var(--ca-text-sm); color: var(--ca-text-secondary); margin-bottom: var(--ca-space-3);">
          Each answer scores 0–4. A domain's maturity is its points over the maximum available
          <em>on the questions you scored</em>. The Corridor Health Index is the weighted mean
          of those domains, renormalised over the weight actually covered.
        </p>
        <div class="table-wrap" style="margin-bottom: var(--ca-space-3);">
          <table>
            <thead><tr><th>Domain</th><th class="right">Weight</th><th class="right">Questions</th></tr></thead>
            <tbody>
              ${DOMAINS.map(
                (d) => html`<tr>
                  <td>${d.name}</td>
                  <td class="right num">${d.weight}%</td>
                  <td class="right num">${d.questions.length}</td>
                </tr>`
              )}
            </tbody>
          </table>
        </div>
        <p style="font-size: var(--ca-text-xs); color: var(--ca-text-tertiary);">
          Two deliberate choices. Skipping a domain lowers your <em>confidence</em> figure
          rather than silently lowering your score — an unanswered domain scored zero at full
          weight would be a made-up number. And options marked “not scored” leave the
          denominator entirely, so an operator with no bonded warehouse is not given a middling
          mark for something they do not do.
        </p>
      </div>
    </section>

    <section class="section">
      <h2 class="section-title">Benchmarks</h2>
      <div class="card">
        <p style="font-size: var(--ca-text-sm); color: var(--ca-text-secondary); margin-bottom: var(--ca-space-3);">
          <span class="num">${corridors}</span> corridors, struck as at ${longDate(BENCHMARK_AS_OF)}.
          Every band carries its derivation, its payload assumption and its provenance, shown
          on screen next to the figure. Three provenance levels are used:
        </p>
        <ul style="margin: 0 0 var(--ca-space-3) var(--ca-space-5); font-size: var(--ca-text-sm); color: var(--ca-text-secondary);">
          <li><strong style="font-weight: var(--ca-weight-medium);">Published tariff</strong> — taken directly from an operator's published rate.</li>
          <li><strong style="font-weight: var(--ca-weight-medium);">Derived</strong> — computed from published guidance, with the arithmetic stated.</li>
          <li><strong style="font-weight: var(--ca-weight-medium);">Contributed</strong> — median of opt-in user submissions. <em>None yet.</em></li>
        </ul>
        <div id="edition-slot"></div>
        <div class="notice notice-warning">
          <span style="flex-shrink:0;margin-top:2px;">${raw(icon('alert', 15))}</span>
          <div style="font-size: var(--ca-text-xs);">
            <strong style="font-weight: var(--ca-weight-medium);">The road bands are derived, not observed.</strong>
            They are computed from Kenya Transporters Association rate guidance and reported
            lane rates, and they show their working — but they are not yet a dataset of actual
            transactions. Treat them as a defensible reference point, not as market truth.
            Rail bands are published KRC tariffs and cover <em>line haul only</em>: first and
            last mile, lift charges and ICD storage must be added before comparing rail to road.
          </div>
        </div>
      </div>
    </section>

    <section class="section">
      <h2 class="section-title">Duty &amp; levies</h2>
      <div class="card">
        <p style="font-size: var(--ca-text-sm); color: var(--ca-text-secondary); margin-bottom: var(--ca-space-3);">
          Assessed in KRA's published order: customs value (CIF) → import duty at the EAC
          Common External Tariff band → excise on CIF plus duty → VAT at 16% of CIF plus duty
          plus excise → Import Declaration Fee at 2.5% of CIF → Railway Development Levy at 2%
          of CIF.
        </p>
        <p style="font-size: var(--ca-text-xs); color: var(--ca-text-tertiary);">
          IDF and RDL are treated by default as levies <em>outside</em> the VAT base. That
          follows the published order of assessment, but practice varies by commodity and by
          how an agent lodges the entry, so the calculator exposes it as a switch rather than
          hiding the judgement inside a constant. Always reconcile against your actual entry.
        </p>
      </div>
    </section>

    <section class="section">
      <h2 class="section-title">Regulatory register — <span class="num">${REGULATIONS.length}</span> instruments</h2>
      <div class="card">
        <p style="font-size: var(--ca-text-sm); color: var(--ca-text-secondary); margin-bottom: var(--ca-space-3);">
          Status is <strong style="font-weight: var(--ca-weight-medium);">computed</strong> from
          today's date against each instrument's effective date — it is never stored. That is
          deliberate: a hardcoded status is how a product ends up asking whether you are
          “ready” for an obligation that took effect eight months ago.
          <span class="num">${inForce}</span> of
          <span class="num">${REGULATIONS.length}</span> are in force today.
        </p>
        <div class="table-wrap">
          <table>
            <thead><tr><th>Instrument</th><th>Effective</th><th>Status</th></tr></thead>
            <tbody>
              ${REGULATIONS.slice()
                .sort((a, b) => new Date(b.effectiveDate) - new Date(a.effectiveDate))
                .map((r) => {
                  const status = statusOf(r);
                  return html`<tr>
                    <td>${r.name}<br /><span style="color: var(--ca-text-quaternary); font-size: var(--ca-text-2xs);">${r.source}</span></td>
                    <td style="white-space: nowrap;">${shortDate(r.effectiveDate)}</td>
                    <td style="white-space: nowrap;">${STATUS_META[status].label}</td>
                  </tr>`;
                })}
            </tbody>
          </table>
        </div>
      </div>
    </section>

    <section class="section">
      <h2 class="section-title">What this cannot do</h2>
      <div class="card">
        <ul style="margin: 0 0 0 var(--ca-space-5); font-size: var(--ca-text-sm); color: var(--ca-text-secondary); line-height: var(--ca-leading-normal);">
          <li>It is <strong style="font-weight: var(--ca-weight-medium);">decision support, not professional advice</strong>. It does not replace a licensed customs agent, an advocate or an accountant.</li>
          <li>It is authoritative on <strong style="font-weight: var(--ca-weight-medium);">Kenyan regulation only</strong>. Corridors terminating in Kampala, Kigali or Dar are modelled for cost and transit; the law of those jurisdictions is not covered.</li>
          <li>It cannot see your actual entry, your actual contract or your actual carrier tariff. Every figure is only as good as what you typed.</li>
          <li>Benchmarks decay. Anything older than six months fails the build, but “passing the build” is not the same as “true this week”.</li>
          <li>It does not broker freight, lend, or move money — by design. That is the failure mode that closed most of this sector's first generation of platforms.</li>
        </ul>
      </div>
    </section>

    <section class="card card-tinted">
      <div style="font-size: var(--ca-text-xs); color: var(--ca-text-tertiary);">
        <strong style="font-weight: var(--ca-weight-medium); color: var(--ca-text-secondary);">
          Kanda — Logistics Advisory
        </strong><br />
        <span class="num">${live}</span> tools live · register current as at ${longDate(BENCHMARK_AS_OF)} ·
        works offline after first load · installable from your browser's menu.
      </div>
    </section>
  `;

  page.querySelectorAll('.link-inline').forEach((btn) => {
    btn.style.cssText =
      'background:none;border:none;padding:0;color:var(--brand-accent-text);cursor:pointer;font:inherit;text-decoration:underline;';
  });
  page.querySelector('#to-saved').addEventListener('click', () => router.navigate('/saved'));

  /* Mounted rather than templated, because the strip owns two clipboard
     and download handlers and an innerHTML string cannot carry them. */
  page.querySelector('#edition-slot').replaceWith(EditionStrip());

  return page;
}
